import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Play, Pause, RefreshCw, CheckCircle, XCircle, Clock, SkipForward,
  Eye, MousePointerClick, Send, Ban, Download, RotateCcw, Info,
} from 'lucide-react';
import { emailService, EmailCampaign, EmailCampaignInsights } from 'api/services/email.service';
import { SentCopyModal } from 'components/Email/SentCopyModal';
import { LoadingSpinner } from 'components/Common/LoadingSpinner';
import { ErrorMessage } from 'components/Common/ErrorMessage';

const REC_STYLES: Record<string, string> = {
  PENDING: 'text-gray-500', SENT: 'text-green-600', FAILED: 'text-red-600', SKIPPED: 'text-orange-500',
};
const REC_FILTERS = ['ALL', 'SENT', 'FAILED', 'SKIPPED', 'PENDING'];
const PAGE_SIZE = 50;

const EmailCampaignDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<EmailCampaign | null>(null);
  const [insights, setInsights] = useState<EmailCampaignInsights | null>(null);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [recStatus, setRecStatus] = useState('ALL');
  const [recSearch, setRecSearch] = useState('');
  const [recPage, setRecPage] = useState(0);
  const [recTotalPages, setRecTotalPages] = useState(0);
  const [recTotal, setRecTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState<{ id: string; email: string } | null>(null);
  const [enrolEmail, setEnrolEmail] = useState('');
  const [enrolNote, setEnrolNote] = useState<string | null>(null);

  const loadRecipients = useCallback(async (status = recStatus, search = recSearch, page = recPage) => {
    if (!id) return;
    const r = await emailService.getRecipientsPage(id, { status, search, page, size: PAGE_SIZE });
    if (r.success && r.data) {
      setRecipients(r.data.content || []);
      setRecTotalPages(r.data.totalPages || 0);
      setRecTotal(r.data.totalElements || 0);
    }
  }, [id, recStatus, recSearch, recPage]);

  const load = useCallback(async () => {
    if (!id) return;
    const [c, i] = await Promise.all([emailService.getCampaign(id), emailService.getInsights(id)]);
    if (c.success) setCampaign(c.data || null); else setError(c.message || 'Failed to load campaign');
    if (i.success) setInsights(i.data || null);
    await loadRecipients();
    setLoading(false);
  }, [id, loadRecipients]);

  useEffect(() => { load(); }, [load]);

  // Poll while the campaign is actively moving, so progress updates without a manual refresh.
  useEffect(() => {
    if (!campaign || !['RUNNING', 'SCHEDULED'].includes(campaign.status || '')) return;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [campaign, load]);

  const act = async (action: 'start' | 'pause' | 'cancel') => {
    if (!id) return;
    if (action === 'cancel'
      && !window.confirm('Stop this campaign for good? Everything still pending will be closed out and cannot be resumed.')) return;
    setBusy(true);
    const res = action === 'start' ? await emailService.startCampaign(id)
      : action === 'pause' ? await emailService.pauseCampaign(id)
      : await emailService.cancelCampaign(id);
    if (!res.success) setError(res.message || `Failed to ${action}`);
    await load();
    setBusy(false);
  };

  const resendFailed = async () => {
    if (!id) return;
    setBusy(true);
    const res = await emailService.resendFailed(id);
    setBusy(false);
    if (!res.success) { setError(res.message || 'Failed to create the resend campaign'); return; }
    // It is created as a DRAFT on purpose — a retry of a batch that just failed
    // is the moment to look at why before sending it again.
    if (res.data?.id) navigate(`/email/campaigns/${res.data.id}`);
  };

  /** Export what is being looked at, filters and all — not just the visible page. */
  const exportCsv = async () => {
    if (!id) return;
    const r = await emailService.getRecipientsPage(id, {
      status: recStatus, search: recSearch, page: 0, size: 100000,
    });
    if (!r.success || !r.data) return;
    const rows = r.data.content || [];
    const csv = ['email,status,reason,message_id,sent_at',
      ...rows.map((x: any) => [
        x.email || '', x.status || '', x.errorCode || '', x.providerMessageId || '',
        x.sentAt ? new Date(x.sentAt).toISOString() : '',
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','))].join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `email-campaign-${id}-${recStatus.toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const enrol = async () => {
    if (!id || !enrolEmail.trim()) return;
    setBusy(true);
    setEnrolNote(null);
    const r = await emailService.enrol(id, { email: enrolEmail.trim() });
    // Says PENDING, not "sent". The caller is deciding whether to chase it, and the
    // scheduler runs once a minute — claiming it had gone would be a lie with a
    // one-minute half-life.
    setEnrolNote(r.success
      ? `${enrolEmail.trim()} enrolled — queued, the scheduler picks it up within a minute.`
      : (r.message || 'Could not enrol that address'));
    if (r.success) { setEnrolEmail(''); await load(); }
    setBusy(false);
  };

  if (loading) return <LoadingSpinner />;
  if (!campaign) return <div className="p-6"><ErrorMessage message={error || 'Campaign not found'} /></div>;

  const total = campaign.totalRecipients || 0;
  const done = (campaign.emailsSent || 0) + (campaign.emailsFailed || 0) + (campaign.emailsSkipped || 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const eng = insights?.engagement;
  // From the funnel, not the campaign counter: a bounce arrives after the send
  // loop has finished, so the counter says 0 while a recipient row says FAILED.
  const failedCount = insights?.funnel.failed ?? (campaign.emailsFailed || 0);

  // Accepted and delivered are DIFFERENT numbers and the difference is the whole
  // point: the provider taking a message is not the recipient receiving it. A bounce
  // arrives minutes later, so a single "Sent" figure is right for about a minute and
  // then quietly overstates delivery for the life of the campaign.
  const accepted = campaign.acceptedByProvider ?? insights?.funnel.sent ?? (campaign.emailsSent || 0);
  const delivered = campaign.deliveredNotBounced ?? null;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <button onClick={() => navigate('/email/campaigns')}
        className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 hover:text-indigo-600">
        <ArrowLeft className="w-4 h-4" /> Back to campaigns
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{campaign.name}</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {campaign.templateName} · from {campaign.fromName || campaign.fromEmail}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} title="Refresh"
            className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800">
            <RefreshCw className="w-4 h-4" />
          </button>
          {failedCount > 0 && (
            <button onClick={resendFailed} disabled={busy}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 disabled:opacity-50">
              <RotateCcw className="w-4 h-4" /> Resend failed ({failedCount})
            </button>
          )}
          {(campaign.status === 'DRAFT' || campaign.status === 'PAUSED') && (
            <button onClick={() => act('start')} disabled={busy}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50">
              <Play className="w-4 h-4" /> Start
            </button>
          )}
          {(campaign.status === 'RUNNING' || campaign.status === 'SCHEDULED') && (
            <button onClick={() => act('pause')} disabled={busy}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded-lg disabled:opacity-50">
              <Pause className="w-4 h-4" /> Pause
            </button>
          )}
          {['RUNNING', 'PAUSED', 'SCHEDULED', 'DRAFT'].includes(campaign.status || '') && (
            <button onClick={() => act('cancel')} disabled={busy}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-red-300 dark:border-red-800 text-red-600 dark:text-red-400 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50">
              <Ban className="w-4 h-4" /> Stop
            </button>
          )}
        </div>
      </div>

      {error && <ErrorMessage message={error} />}

      {/* Delivery */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat icon={<Clock className="w-4 h-4" />} label="Total" value={total} tone="text-gray-700 dark:text-gray-200" />
        <Stat icon={<Send className="w-4 h-4" />} label="Accepted" value={accepted} tone="text-green-600"
          sub="taken by the provider" />
        {delivered != null && (
          <Stat icon={<CheckCircle className="w-4 h-4" />} label="Delivered" value={delivered} tone="text-emerald-600"
            sub="accepted, minus bounces" />
        )}
        <Stat icon={<XCircle className="w-4 h-4" />} label="Bounced / failed" value={campaign.bouncedOrFailed ?? failedCount} tone="text-red-600" />
        <Stat icon={<SkipForward className="w-4 h-4" />} label="Skipped" value={insights?.funnel.skipped ?? (campaign.emailsSkipped || 0)} tone="text-orange-500" />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5">
        <div className="flex justify-between text-sm mb-2 text-gray-600 dark:text-gray-300">
          <span>Status: <strong>{campaign.status}</strong></span>
          <span>{done} / {total} ({pct}%)</span>
        </div>
        <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
          <div className="h-full bg-indigo-600 transition-all" style={{ width: `${pct}%` }} />
        </div>
      </div>

      {/* A standing campaign is a bucket, not a run — say so where somebody would
          otherwise wait for it to finish. */}
      {campaign.standing && (
        <div className="bg-violet-50 dark:bg-violet-900/20 border border-violet-200 dark:border-violet-800 rounded-xl p-5 space-y-3">
          <div>
            <h2 className="font-medium text-violet-900 dark:text-violet-200">Standing campaign</h2>
            <p className="text-sm text-violet-800/80 dark:text-violet-300/80 mt-1">
              This one stays open and never completes. Add people one at a time — from here, from a
              form, or from an agent after a call — and each is sent with the same tracking,
              suppression and reporting as a bulk run.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="email"
              value={enrolEmail}
              onChange={(e) => setEnrolEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') enrol(); }}
              placeholder="someone@example.com"
              className="flex-1 min-w-[16rem] px-3 py-2 text-sm rounded-lg border border-violet-300 dark:border-violet-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
            />
            <button
              onClick={enrol}
              disabled={busy || !enrolEmail.trim()}
              className="px-3 py-2 text-sm rounded-lg bg-violet-600 text-white hover:bg-violet-700 disabled:opacity-50"
            >
              Add to campaign
            </button>
          </div>
          {enrolNote && <p className="text-sm text-violet-900 dark:text-violet-200">{enrolNote}</p>}
        </div>
      )}

      {/* Engagement */}
      {eng && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 space-y-4">
          <h2 className="font-medium text-gray-800 dark:text-gray-100">Engagement</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {/* Was labelled "Delivered to inbox" while showing the ACCEPTED count —
                which is the provider taking it, not the recipient receiving it. */}
            <Stat icon={<Send className="w-4 h-4" />} label={delivered != null ? 'Delivered' : 'Accepted'}
              value={delivered ?? accepted} tone="text-gray-700 dark:text-gray-200" />
            <Stat icon={<Eye className="w-4 h-4" />} label="Opened (unique)" value={eng.uniqueOpens}
              tone="text-blue-600" sub={eng.openRate != null ? `${eng.openRate}% of sent` : undefined} />
            <Stat icon={<Eye className="w-4 h-4" />} label="Opened by a person" value={eng.uniqueOpensExcludingMachineFetches}
              tone="text-blue-500" sub="excludes mail-privacy proxies" />
            <Stat icon={<MousePointerClick className="w-4 h-4" />} label="Clicked (unique)" value={eng.uniqueClicks}
              tone="text-indigo-600" sub={eng.clickRate != null ? `${eng.clickRate}% of sent` : undefined} />
          </div>
          {eng.clickToOpenRate != null && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              <strong>{eng.clickToOpenRate}%</strong> of the people who opened went on to click.
            </p>
          )}
          {/* The caveat travels with the number, not in a wiki nobody opens. */}
          <p className="flex items-start gap-2 text-[11px] text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded-lg p-2.5">
            <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {eng.caveat}
          </p>
        </div>
      )}

      {/* Why sends failed or were skipped */}
      {insights && (insights.errorAnalysis.length > 0 || insights.skipAnalysis.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <ReasonTable title="Why sends failed" rows={insights.errorAnalysis} tone="text-red-600" />
          <ReasonTable title="Why recipients were skipped" rows={insights.skipAnalysis} tone="text-orange-500" />
        </div>
      )}

      {/* Which links were clicked */}
      {insights && insights.links.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-200">
            Links clicked
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-left">
                <tr>
                  <th className="px-4 py-2 font-medium">URL</th>
                  <th className="px-4 py-2 font-medium w-24">Clicks</th>
                  <th className="px-4 py-2 font-medium w-24">People</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {insights.links.map((l) => (
                  <tr key={l.url}>
                    <td className="px-4 py-2 text-gray-800 dark:text-gray-200 break-all">{l.url}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{l.clicks}</td>
                    <td className="px-4 py-2 text-gray-600 dark:text-gray-300">{l.uniqueClicks}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recipients */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between gap-3 flex-wrap">
          <span className="font-medium text-gray-700 dark:text-gray-200">Recipients ({recTotal})</span>
          <div className="flex items-center gap-2 flex-wrap">
            <input value={recSearch} placeholder="Search address"
              onChange={(e) => setRecSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setRecPage(0); loadRecipients(recStatus, recSearch, 0); } }}
              className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200" />
            <select value={recStatus}
              onChange={(e) => { setRecStatus(e.target.value); setRecPage(0); loadRecipients(e.target.value, recSearch, 0); }}
              className="px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200">
              {REC_FILTERS.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
            <button onClick={exportCsv} title="Export what is filtered, not just this page"
              className="flex items-center gap-1.5 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-600 dark:text-gray-300">
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        </div>
        <div className="max-h-96 overflow-x-auto overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-gray-500 dark:text-gray-400 text-left sticky top-0">
              <tr>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Detail</th>
                <th className="px-4 py-2 font-medium text-right">Copy</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {recipients.map((r) => (
                <tr key={r.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                  <td className="px-4 py-2 text-gray-800 dark:text-gray-200">{r.email}</td>
                  <td className={`px-4 py-2 font-medium ${REC_STYLES[r.status] || 'text-gray-500'}`}>{r.status}</td>
                  <td className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400">{r.errorCode || r.providerMessageId || ''}</td>
                  <td className="px-4 py-2 text-right">
                    {/* Only where there is something to show. Offering "View sent" on a
                        PENDING row invites a click that can only disappoint. */}
                    {r.status === 'SENT' || r.status === 'FAILED' ? (
                      <button
                        onClick={() => setViewing({ id: r.id, email: r.email })}
                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                      >
                        View sent
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
              {recipients.length === 0 && (
                <tr><td colSpan={4} className="px-4 py-6 text-center text-gray-400">No recipients match this filter</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {recTotalPages > 1 && (
          <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700 flex items-center justify-between text-sm">
            <button disabled={recPage === 0}
              onClick={() => { const p = recPage - 1; setRecPage(p); loadRecipients(recStatus, recSearch, p); }}
              className="px-2 py-1 text-gray-600 dark:text-gray-300 disabled:opacity-40">Previous</button>
            <span className="text-gray-500 dark:text-gray-400">Page {recPage + 1} of {recTotalPages}</span>
            <button disabled={recPage + 1 >= recTotalPages}
              onClick={() => { const p = recPage + 1; setRecPage(p); loadRecipients(recStatus, recSearch, p); }}
              className="px-2 py-1 text-gray-600 dark:text-gray-300 disabled:opacity-40">Next</button>
          </div>
        )}
      </div>

      {viewing && id && (
        <SentCopyModal
          campaignId={id}
          recipientId={viewing.id}
          email={viewing.email}
          onClose={() => setViewing(null)}
        />
      )}
    </div>
  );
};

const Stat: React.FC<{ icon: React.ReactNode; label: string; value: number; tone: string; sub?: string }> =
  ({ icon, label, value, tone, sub }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">{icon}{label}</div>
    <div className={`text-2xl font-semibold mt-1 ${tone}`}>{value}</div>
    {sub && <div className="text-[11px] text-gray-400 mt-0.5">{sub}</div>}
  </div>
);

const ReasonTable: React.FC<{ title: string; rows: { reason: string; count: number }[]; tone: string }> =
  ({ title, rows, tone }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
    <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 font-medium text-gray-700 dark:text-gray-200">
      {title}
    </div>
    {rows.length === 0 ? (
      <p className="px-4 py-4 text-sm text-gray-400">None</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
            {rows.map((r) => (
              <tr key={r.reason}>
                <td className="px-4 py-2 text-gray-700 dark:text-gray-200">{r.reason}</td>
                <td className={`px-4 py-2 text-right font-medium w-20 ${tone}`}>{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export default EmailCampaignDetail;
